"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
export default function PourquoiChoisirOtSolution() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const sectionRef = useRef<HTMLElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      const node = sectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewport = window.innerHeight;
      const progress = 1 - (rect.top + rect.height / 2) / (viewport + rect.height / 2);
      const clamped = Math.max(0, Math.min(1, progress));
      const rot = (clamped - 0.5) * 30;
      setRotation(rot);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const markers = [{
    id: 1,
    label: "01",
    title: "Buse Haute Pression",
    spec: "Acier Inoxydable",
    description: "Jet rotatif calibré pour désagréger les amas de graisse les plus tenaces.",
    top: "12%",
    left: "18%"
  }, {
    id: 2,
    label: "02",
    title: "Capteur Optique HD",
    spec: "Lentille Saphir",
    description: "Transmission en temps réel pour localiser les fissures et racines.",
    top: "34%",
    left: "82%"
  }, {
    id: 3,
    label: "03",
    title: "Sonde de Localisation",
    spec: "Fréquence 512Hz",
    description: "Permet un repérage précis en surface pour éviter les excavations inutiles.",
    top: "58%",
    left: "14%"
  }, {
    id: 4,
    label: "04",
    title: "Câble de Poussée",
    spec: "Fibre de Verre",
    description: "Rigidité optimisée pour naviguer dans les coudes à 90 degrés.",
    top: "78%",
    left: "84%"
  }];
  const features = [{
    spec: "4K ULTRA HD",
    title: "Inspection par Caméra",
    description: "Diagnostic visuel complet de vos conduits pour identifier la cause exacte du blocage sans aucune devinette."
  }, {
    spec: "5000 PSI",
    title: "Débouchage Hydro-Jet",
    description: "Nettoyage chirurgical des parois internes du drain, éliminant calcaire, racines et sédiments accumulés."
  }, {
    spec: "24/7 DISPO",
    title: "Intervention d'Urgence",
    description: "Équipe mobile prête à intervenir à Montréal, Laval et Longueuil pour tout drain bouché critique."
  }, {
    spec: "DÈS 275$",
    title: "Tarification Transparente",
    description: "Des solutions spécialisées à prix fixe, sans frais cachés, pour une tranquillité d'esprit totale."
  }];
  return <section data-section-id="2862" id="pourquoi-choisir-ot-solution" ref={sectionRef} className="relative bg-background text-foreground overflow-hidden py-20 md:py-32 lg:py-40">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <div className="mb-12 md:mb-20 flex items-center justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <span className="flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-primary" aria-hidden="true" />
            [04] INGÉNIERIE DE PRÉCISION
          </span>
          <span className="hidden md:inline">OT SOLUTION — NOTES TECHNIQUES</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-6 relative transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-full h-[520px] md:h-[640px] lg:h-[700px]">
              {}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <line x1="50" y1="20" x2="18" y2="12" stroke="var(--primary)" strokeWidth="0.15" strokeDasharray="0.5 0.5" opacity="0.4" />
                <line x1="50" y1="40" x2="82" y2="34" stroke="var(--primary)" strokeWidth="0.15" strokeDasharray="0.5 0.5" opacity="0.4" />
                <line x1="50" y1="60" x2="14" y2="58" stroke="var(--primary)" strokeWidth="0.15" strokeDasharray="0.5 0.5" opacity="0.4" />
                <line x1="50" y1="78" x2="84" y2="78" stroke="var(--primary)" strokeWidth="0.15" strokeDasharray="0.5 0.5" opacity="0.4" />
              </svg>

              {}
              <div className="absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out" style={{
              transform: `rotate(${rotation}deg)`
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694820/img/drain_inspection_camera_tech.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694820/img/drain_inspection_camera_tech.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694820/img/drain_inspection_camera_tech.png-wvc-sizes" alt="Vue éclatée de l'équipement d'inspection caméra haute définition OT Solution" loading="lazy" decoding="async" className="w-full h-full object-contain" />
              </div>

              {}
              {markers.map((marker, i) => <div key={marker.id} data-index={i} className="absolute" style={{
              top: marker.top,
              left: marker.left,
              transform: "translate(-50%, -50%)"
            }}>
                  <HoverCard openDelay={100} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Button variant="ghost" size="sm" onMouseEnter={() => setActiveMarker(marker.id)} onMouseLeave={() => setActiveMarker(null)} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors h-auto p-0" aria-label={`Composant ${marker.label}: ${marker.title}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-md transition-all duration-300 ${activeMarker === marker.id ? "bg-primary shadow-[0_0_12px_var(--primary)] scale-150" : "bg-foreground"}`} aria-hidden="true" />
                        <span>{marker.label}</span>
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent align="start" sideOffset={8} className="w-64 bg-card border border-border rounded-md shadow-lg">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">
                        {marker.label} — {marker.spec}
                      </div>
                      <div className="font-default font-semibold text-lg text-foreground mb-2 leading-tight">
                        {marker.title}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {marker.description}
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </div>)}
            </div>

            {}
            <div className="mt-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>Fig. 04 — Assemblage Technique Caméra</span>
              <span>Survoler pour inspecter</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 lg:pl-4 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="font-default font-semibold text-foreground text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] tracking-tight">
              Chaque millimètre
              <span className="text-primary"> analysé.</span>
              <br />
              Chaque drain,
              <span className="text-primary"> libéré.</span>
            </h2>

            <p className="mt-8 max-w-md text-base text-muted-foreground leading-relaxed">
              Pas de devinettes. Pas de solutions temporaires. Chaque intervention est guidée par une technologie de pointe et une expertise spécialisée — parce que la clarté de vos conduits est notre seule priorité.
            </p>

            <div className="mt-12 space-y-0">
              {features.map((feature, i) => <div key={feature.title} data-index={i}>
                  {i > 0 && <Separator className="bg-border/40" />}
                  <div className="grid grid-cols-12 gap-4 py-7 group">
                    <div className="col-span-4 md:col-span-3">
                      <div className="font-mono text-sm text-primary font-medium tracking-wider">
                        {feature.spec}
                      </div>
                    </div>
                    <div className="col-span-8 md:col-span-9">
                      <h3 className="font-default font-semibold text-lg md:text-xl text-foreground mb-2 leading-tight">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>)}
              <Separator className="bg-border/40" />
            </div>

            {}
            <div className="mt-12">
              <Button asChild variant="outline" size="lg" className="bg-primary border-none text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-widest px-8 h-12 rounded-md transition-all shadow-md">
                <Link to="/ContactPage">
                  Demander une estimation
                  <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}